import { useEffect, useState } from "react";
import { Plus, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SegmentedButtons } from "@/components/ui/segmented";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CampoData from "@/components/common/CampoData";
import { erroreDi, giornoCorto } from "@/lib/crm";
import { oggiRoma } from "@/lib/soldi";

/* «Aggiorna il percorso» (c45): per i clienti seguiti fuori dal gestionale si segnano a mano le fasi già
   avvenute, tante in una schermata e un salvataggio solo. Le fasi a mano dicono dov'è il cliente e non creano
   preventivi, contratti né incassi: il fatto vero vince sempre, lo decide il server. Il server dice per ogni
   tappa se chi guarda può segnarla (`segnabile`) e, se no, perché (`perche`). I buchi prima della fase scelta
   sono una guida, non un controllo: il permesso sta nel database. */

const ESITI = [
  { id: "fatto", label: "Fatto" },
  { id: "saltato", label: "Non c'è stato" },
];

const offerta = (p) => p.segnabile && (p.stato !== "fatto" || p.a_mano);
const apertaDavvero = (p) => p.stato === "spento" && !p.a_mano;
const statoDi = (p) =>
  p.a_mano?.esito === "fatto"
    ? "fatto a mano"
    : p.a_mano?.esito === "saltato"
      ? "non c'è stato"
      : p.stato === "in_arrivo"
        ? (p.codice === "preventivo" ? "in preparazione" : "in agenda")
        : "non ancora";
const cosaDice = (a) => (a.esito === "saltato" ? "non c'è stato" : a.giorno ? `fatto il ${giornoCorto(a.giorno)}` : "fatto, data non segnata");

let seme = 0;
// `toccata`: la riga con cui si apre la finestra è una proposta, non una scelta. Finché nessuno la tocca, un «Togli» la porta via con sé.
const riga = (p, toccata = true) => ({
  chiave: ++seme,
  toccata,
  tappa: p.codice,
  esito: p.a_mano?.esito || "fatto",
  giorno: p.a_mano?.giorno || "",
  nota: p.a_mano?.nota || "",
});

const SELECT =
  "h-11 w-full rounded-md border border-line-input bg-surface px-3 text-body text-ink transition-colors duration-150 ease-enter focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:bg-surface-3";

export default function AggiornaPercorso({ open, onOpenChange, passi, clienteId, puoFattoFuori, onFatto, onFattoFuori }) {
  const [righe, setRighe] = useState([]);
  const [togli, setTogli] = useState([]);
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState(null);
  const oggi = oggiRoma();

  useEffect(() => {
    if (!open) return;
    const prossima = passi.find((p) => offerta(p) && apertaDavvero(p));
    setRighe(prossima ? [riga(prossima, false)] : []);
    setTogli([]);
    setErrore(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- la scheda si ricarica a salvataggio fatto: non deve azzerare il modulo

  const per = Object.fromEntries(passi.map((p) => [p.codice, p]));
  const usate = new Set(righe.map((r) => r.tappa));
  const aMano = passi.filter((p) => p.a_mano && p.segnabile);

  /* Le fasi che prima di quella scelta sono ancora aperte e nessuna riga copre: da chiedere (le può segnare chi guarda)
     o solo da dire (con il loro perché). Una tappa in agenda è già nel suo corso: non conta come aperta. */
  const prima = (r) => {
    const aperte = passi.slice(0, passi.findIndex((p) => p.codice === r.tappa)).filter((p) => apertaDavvero(p) && !usate.has(p.codice) && !togli.includes(p.codice));
    return { daChiedere: aperte.filter((p) => p.segnabile), solo: aperte.filter((p) => !p.segnabile && p.perche) };
  };

  const cambia = (i, quanto) => setRighe((rs) => rs.map((r, j) => (j === i ? { ...r, ...quanto, toccata: true } : r)));
  // La chiave resta: cambiando fase la riga non si rimonta e il fuoco non si perde.
  const scegli = (i, codice) => setRighe((rs) => rs.map((x, j) => (j === i ? { ...riga(per[codice]), chiave: x.chiave } : x)));
  const via = (i) => setRighe((rs) => rs.filter((_, j) => j !== i));
  const aggiungiAperte = (i) =>
    setRighe((rs) => [...rs.slice(0, i), ...prima(rs[i]).daChiedere.map(riga), ...rs.slice(i)]);

  const libere = passi.filter((p) => offerta(p) && !usate.has(p.codice) && !togli.includes(p.codice));
  const ultima = righe.length ? passi.findIndex((p) => p.codice === righe[righe.length - 1].tappa) : -1;
  const prossimaLibera = libere.find((p) => apertaDavvero(p) && passi.indexOf(p) > ultima) || libere.find(apertaDavvero) || libere[0];

  const togliA = (codice) => {
    setTogli((t) => [...t, codice]);
    setRighe((rs) => rs.filter((r) => r.tappa !== codice && r.toccata));
  };

  const senzaRisposta = righe.some((r) => prima(r).daChiedere.length > 0);
  const nelFuturo = righe.some((r) => r.esito === "fatto" && r.giorno > oggi);
  const nulla = !righe.length && !togli.length;

  const salva = async (e) => {
    e.preventDefault();
    if (senzaRisposta || nelFuturo || nulla) return;
    setBusy(true);
    setErrore(null);
    try {
      const r = await api.post(`/crm/clienti/${clienteId}/percorso`, {
        righe: righe.map((x) => ({ tappa: x.tappa, esito: x.esito, giorno: x.esito === "fatto" && x.giorno ? x.giorno : null, nota: x.nota.trim() || null })),
        togli,
      });
      toast.success("Percorso aggiornato");
      onOpenChange(false);
      onFatto(r.data);
    } catch (err) {
      setErrore(erroreDi(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (busy ? null : onOpenChange(v))}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto" data-testid="percorso-modal">
        <form onSubmit={salva} className="grid gap-card">
          <DialogHeader>
            <DialogTitle>Aggiorna il percorso</DialogTitle>
            <DialogDescription>
              Segna le fasi già avvenute fuori dal gestionale, così il percorso dice dov&apos;è il cliente. Non entrano nei numeri dei soldi:
              preventivo e contratto ci entrano con «Fatto fuori».
            </DialogDescription>
          </DialogHeader>

          {righe.length ? (
            <ol className="grid gap-card" aria-label="Le fasi da segnare">
              {righe.map((r, i) => {
                const p = per[r.tappa];
                const { daChiedere, solo: tutte } = prima(r);
                // Il perché di una fase che da qui non si segna si dice una volta, sotto la prima riga a cui riguarda.
                const solo = tutte.filter((x) => !righe.slice(0, i).some((prec) => prima(prec).solo.some((y) => y.codice === x.codice)));
                const opzioni = passi.filter((x) => x.codice === r.tappa || (offerta(x) && !usate.has(x.codice) && !togli.includes(x.codice)));
                const inAgenda = p.stato === "in_arrivo";
                return (
                  <li key={r.chiave} className="grid gap-group border-l-2 border-line pl-group" data-testid={`percorso-riga-${i}`}>
                    <div className="grid gap-inside">
                      <div className="flex items-center justify-between gap-inside">
                        <Label htmlFor={`percorso-fase-${r.chiave}`}>Fase</Label>
                        {righe.length > 1 || togli.length ? (
                          <Button type="button" variant="ghost" size="icon" className="-my-2 size-11 text-ink-2" onClick={() => via(i)} aria-label={`Togli la riga ${p.titolo}`} data-testid={`percorso-riga-${i}-via`}>
                            <X aria-hidden />
                          </Button>
                        ) : null}
                      </div>
                      <select id={`percorso-fase-${r.chiave}`} className={SELECT} value={r.tappa} onChange={(e) => scegli(i, e.target.value)} data-testid={`percorso-riga-${i}-tappa`}>
                        {opzioni.map((x) => (
                          <option key={x.codice} value={x.codice}>
                            {x.titolo} · {statoDi(x)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <SegmentedButtons
                      aria-label={`Com'è andata: ${p.titolo}`}
                      voci={inAgenda ? ESITI.slice(0, 1) : ESITI}
                      valore={inAgenda ? "fatto" : r.esito}
                      onCambia={(esito) => cambia(i, { esito, giorno: esito === "fatto" ? r.giorno : "" })}
                      prefisso={`percorso-riga-${i}`}
                      className="w-full"
                    />

                    {r.esito === "fatto" ? (
                      <div className="grid gap-inside">
                        <Label htmlFor={`percorso-giorno-${r.chiave}`}>Il giorno, se lo sai</Label>
                        <CampoData id={`percorso-giorno-${r.chiave}`} value={r.giorno} max={oggi} onChange={(e) => cambia(i, { giorno: e.target.value })} testId={`percorso-riga-${i}-giorno`} />
                        {r.giorno > oggi ? <p className="text-small text-warning-ink">Il giorno non può essere dopo oggi.</p> : null}
                      </div>
                    ) : null}

                    <div className="grid gap-inside">
                      <Label htmlFor={`percorso-nota-${r.chiave}`}>
                        Nota <span className="font-normal text-ink-2">(facoltativa)</span>
                      </Label>
                      <Input id={`percorso-nota-${r.chiave}`} value={r.nota} maxLength={300} autoComplete="off" onChange={(e) => cambia(i, { nota: e.target.value })} data-testid={`percorso-riga-${i}-nota`} />
                    </div>

                    {puoFattoFuori && (r.tappa === "preventivo" || r.tappa === "contratto") ? (
                      <button type="button" onClick={() => onFattoFuori(r.tappa)} className="min-h-11 justify-self-start text-left text-small font-semibold text-brand-ink hover:underline md:min-h-8" data-testid={`percorso-riga-${i}-fuori`}>
                        Ho il foglio: Fatto fuori
                      </button>
                    ) : null}

                    {daChiedere.length || solo.length ? (
                      <div className="grid gap-inside rounded-md bg-surface-2 p-group text-small" role="status" data-testid={`percorso-aperte-${i}`}>
                        {daChiedere.length ? (
                          <>
                            <p className="text-ink">
                              Prima di «{p.titolo}» restano aperte: {daChiedere.map((x) => x.titolo).join(", ")}. Cosa è successo?
                            </p>
                            <Button type="button" variant="outline" size="sm" className="h-11 justify-self-start md:h-9" onClick={() => aggiungiAperte(i)} data-testid={`percorso-aggiungi-aperte-${i}`}>
                              Aggiungi queste fasi
                            </Button>
                          </>
                        ) : null}
                        {solo.map((x) => (
                          <p key={x.codice} className="text-ink-2">
                            {x.titolo}: {x.perche}
                          </p>
                        ))}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="text-ink-2" data-testid="percorso-niente">
              {aMano.length && !togli.length ? "Qui sotto le fasi già segnate a mano." : "Non c'è altro da segnare."}
            </p>
          )}

          {prossimaLibera ? (
            <Button type="button" variant="outline" className="justify-self-start" onClick={() => setRighe((rs) => [...rs, riga(prossimaLibera)])} data-testid="percorso-altra">
              <Plus />
              Aggiorna un&apos;altra fase
            </Button>
          ) : null}

          {aMano.length ? (
            <section className="grid gap-inside border-t border-line-subtle pt-group" aria-label="Fasi segnate a mano">
              <h3 className="font-semibold text-ink-strong">Già segnate a mano</h3>
              <ul className="grid">
                {aMano.map((p) => {
                  const tolta = togli.includes(p.codice);
                  return (
                    <li key={p.codice} className="flex min-h-11 items-center justify-between gap-group">
                      <span className={tolta ? "text-ink-3 line-through" : "text-ink"}>
                        {p.titolo} · {tolta ? "sarà tolta" : cosaDice(p.a_mano)}
                      </span>
                      {tolta ? (
                        <Button type="button" variant="ghost" size="sm" className="h-11 md:h-9" onClick={() => setTogli((t) => t.filter((c) => c !== p.codice))} data-testid={`percorso-rimetti-${p.codice}`}>
                          <RotateCcw />
                          Rimetti
                        </Button>
                      ) : (
                        <Button type="button" variant="ghost" size="sm" className="h-11 md:h-9" onClick={() => togliA(p.codice)} aria-label={`Togli ${p.titolo}`} data-testid={`percorso-togli-${p.codice}`}>
                          Togli
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          <DialogFooter className="gap-inside">
            <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={busy || senzaRisposta || nelFuturo || nulla} data-testid="percorso-salva">
              {busy ? "Salvo…" : "Salva"}
            </Button>
          </DialogFooter>
          {errore ? (
            <p role="alert" className="rounded-sm bg-danger-bg px-3 py-2.5 text-small text-danger-ink" data-testid="percorso-errore">
              {errore}
            </p>
          ) : null}
        </form>
      </DialogContent>
    </Dialog>
  );
}
